import { useEffect, useRef, useState } from 'react';
import type { HouseholdInput } from '@/types';

export type HouseholdFormTargets = {
  setPrimary: (p: HouseholdInput['primary']) => void;
  setPartner: (p: NonNullable<HouseholdInput['partner']>) => void;
  setIncludePartner: (b: boolean) => void;
  setChildren: (c: NonNullable<HouseholdInput['children']>) => void;
  setClaimsChildBenefit: (b: boolean) => void;
  setPreferredStrategy?: (strategy: HouseholdInput['preferred_strategy']) => void;
};

export function usePopulateHouseholdForm(
  household: HouseholdInput | null,
  householdState: HouseholdFormTargets
): boolean {
  const populatedRef = useRef(false);
  const [hasPopulated, setHasPopulated] = useState(false);
  const previousHousehold = useRef<HouseholdInput | null>(null);

  useEffect(() => {
    if (previousHousehold.current === household) {
      return;
    }

    populatedRef.current = false;
    setHasPopulated(false);
    previousHousehold.current = household;
  }, [household]);

  useEffect(() => {
    if (!household || populatedRef.current) return;

    const {
      setPrimary,
      setPartner,
      setIncludePartner,
      setChildren,
      setClaimsChildBenefit,
      setPreferredStrategy,
    } = householdState;

    setPrimary(household.primary);

    if (household.partner) {
      setPartner(household.partner);
      setIncludePartner(true);
    } else {
      setIncludePartner(false);
    }

    setChildren(household.children || []);
    setClaimsChildBenefit(household.claims_child_benefit ?? false);
    if (setPreferredStrategy) {
      setPreferredStrategy(household.preferred_strategy ?? null);
    }

    populatedRef.current = true;
    setHasPopulated(true);
  }, [household, householdState]);

  return hasPopulated;
}
